import React, { useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck } from "lucide-react";
import SelectionFeedback from "./SelectionFeedback";
import { NGROK } from "../../../../APIs";
import {
  sortOrderLinuxProfiles,
  sortOrderMacProfiles,
  sortOrderWindowsProfiles,
} from "../../../../services/Helpers";

const ProfileSelection = ({
  setSelectedProfile,
  selectedTenant,
  os,
  selectedProfile,
}) => {
  const [profileList, setProfileList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(false);
    const fetchTenantProfiles = async (tenantName) => {
      try {
        const response = await axios.get(
          `${NGROK}/api/profiles/inherited?tenantName=${tenantName}`,
        );

        const linuxProfiles = response.data
          .filter((profile) => profile.os === "linux")
          .sort((a, b) => {
            const aIndex = sortOrderLinuxProfiles.indexOf(a.name);
            const bIndex = sortOrderLinuxProfiles.indexOf(b.name);
            return aIndex - bIndex;
          });
        const windowsProfiles = response.data
          .filter((profile) => profile.os === "windows")
          .sort((a, b) => {
            const aIndex = sortOrderWindowsProfiles.indexOf(a.name);
            const bIndex = sortOrderWindowsProfiles.indexOf(b.name);
            return aIndex - bIndex;
          });
        const macProfiles = response.data
          .filter((profile) => profile.os === "mac")
          .sort((a, b) => {
            const aIndex = sortOrderMacProfiles.indexOf(a.name);
            const bIndex = sortOrderMacProfiles.indexOf(b.name);
            return aIndex - bIndex;
          });

        if (!mounted) return;
        if (os.toLowerCase() === "windows") setProfileList(windowsProfiles);
        else if (os.toLowerCase() === "linux") setProfileList(linuxProfiles);
        else if (os.toLowerCase() === "macos") setProfileList(macProfiles);
      } catch (error) {
        console.error(error);
        if (mounted) {
          setProfileList([]);
          setError(true);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (selectedTenant) fetchTenantProfiles(selectedTenant.tenantName);
    else setLoading(false);
    return () => {
      mounted = false;
    };
  }, [os, selectedTenant]);

  useEffect(() => {
    if (profileList.length) setSelectedProfile(profileList[0]);
  }, [profileList, setSelectedProfile]);

  if (loading || error || !profileList.length || !selectedProfile)
    return (
      <SelectionFeedback
        loading={loading}
        error={error}
        title={error ? "Unable to load profiles" : "No comparison profiles"}
        description={
          error
            ? "Go back and return to this step to retry."
            : "No profiles are available for this tenant and operating system. Review your report scope before continuing."
        }
      />
    );

  return (
    <div
      className="op-choice-grid"
      role="radiogroup"
      aria-label="Comparison profile"
    >
      {profileList.map((profile) => (
        <label
          key={profile.name}
          className={`op-choice ${selectedProfile.name === profile.name ? "is-selected" : ""}`}
        >
          <input
            type="radio"
            name="comparison-profile"
            value={profile.name}
            checked={selectedProfile.name === profile.name}
            onChange={() => setSelectedProfile(profile)}
          />
          <ShieldCheck aria-hidden="true" />
          <span>
            <strong>{profile.name}</strong>
            <small>{profile.os}</small>
          </span>
        </label>
      ))}
    </div>
  );
};

export default ProfileSelection;
